import { type MouseEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import { trpc, useMutation } from 'client/lib/trpc';
import type { OnLog } from 'client/state/LogState';
import { usePodInfoState } from 'client/state/PodInfoState';

interface ComponentStatusHeaderProps {
  deploymentID: number | null | undefined;
  deploymentUrl: string;
  namespace: string;
  pod?: string;
  container?: string;
  onLog?: OnLog;
}

export const useContainerActions = ({ deploymentID, deploymentUrl, namespace, pod, container, onLog }: ComponentStatusHeaderProps) => {
  const [copyTooltip, setCopyTooltip] = useState<string | null>(null);
  const { showDialog: showPodInfoDialog } = usePodInfoState();

  const logPath = useMemo(() => {
    // TODO: Probably not sustainable way of getting url
    let path = `${deploymentUrl}/logs/container/${namespace}`;
    if (pod) path += `/${pod}`;
    if (pod && container) path += `/${container}`;
    return path;
  }, [deploymentUrl, namespace]);

  const sshMutation = useMutation(trpc.deployment.ssh, {
    onSuccess: (data) => {
      if (data && data.status === 'ok' && data.sshConnectionString) {
        if (navigator?.clipboard) {
          // Copy the command early if we have a success
          navigator.clipboard.writeText(`ssh ${data.sshConnectionString}`);
        }

        // Add artificial delay after a successful fetch to give the change some time to propagate
        // TODO! Delay removed here since we retry in core instead, delete this later
        return new Promise((resolve) => setTimeout(resolve, 400));
      }
    },
    onSettled: async (data) => data && handleCopy(data),
  });

  const restartPodMutation = useMutation(trpc.deployment.restartPod, {
    onSuccess: (data) => {
      if (data && data.status === 'ok') {
        // Add artificial delay after a successful fetch to keep loading indicator spinning
        return new Promise((resolve) => setTimeout(resolve, 2000));
      }
    },
  });

  const describePodMutation = useMutation(trpc.deployment.describePod, {
    onSuccess: (data) => {
      // Tuomas: What to do here, we got the info at least
      showPodInfoDialog(data as unknown as any /* issue when date object become strings in json conversion */);
    },
  });

  const handleCopy = ({ status, sshConnectionString }: { status: string; sshConnectionString?: string }) => {
    if (status !== 'ok' || !sshConnectionString) {
      setCopyTooltip('Failed to fetch SSH command');
      return;
    }

    if (navigator?.clipboard) {
      navigator.clipboard.writeText(`ssh ${sshConnectionString}`);
      setCopyTooltip('SSH command copied to the clipboard');
    } else {
      setCopyTooltip('Failed to copy SSH command. Check clipboard permission.');
    }
  };

  const handleLog: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      console.log('handleLog', onLog, event);
      if (!onLog) return;
      event.stopPropagation();
      event.preventDefault();
      onLog({ type: 'container', level1: namespace, level2: pod, level3: container });
    },
    [namespace, pod, container, onLog]
  );

  const handleRestart = useCallback(() => {
    if (!deploymentID) return;
    if (!pod) return;
    restartPodMutation.mutate({ deploymentID, namespace, podName: pod });
  }, [deploymentID, namespace, pod]);

  const handleSsh = useCallback(() => {
    if (!deploymentID) return;
    if (!pod) return;
    sshMutation.mutate({ deploymentID, namespace, pod, container });
  }, [deploymentID, namespace, pod, container]);

  const handleDescribe = useCallback(() => {
    if (!deploymentID) return;
    if (!pod) return;
    describePodMutation.mutate({ deploymentID, namespace, podName: pod });
  }, [deploymentID, namespace, pod]);

  useEffect(() => {
    if (!copyTooltip) return;

    const timeout = setTimeout(() => setCopyTooltip(null), 3000);
    return () => clearTimeout(timeout);
  }, [copyTooltip]);

  return {
    logPath,
    copyTooltip,
    handleLog,
    handleRestart,
    handleSsh,
    handleDescribe,
    isRestartLoading: restartPodMutation.isLoading,
    isSshLoading: sshMutation.isLoading,
  };
};
